import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faEye,
  faSearch,
  faShieldAlt,
  faUsers,
  faBroadcastTower,
  faRobot,
  faFileAlt,
  faComments,
  faCog,
  faCreditCard,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { getToken } from "../../function/token";
import domain from "../../../reusecomponent/domain";
import Swal from "sweetalert2";
import Preloader from "../preloader";
import { useNavigate } from "react-router-dom";

function UserRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const [permissionStructure, setPermissionStructure] = useState({});

  // Fetch permissions from backend
  const fetchPermissions = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${domain()}/agen_role/permission_list`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      // Console log the entire API response
      console.log("=== PERMISSION LIST API RESPONSE ===");
      console.log("Full API Response:", data);
      console.log("Response Status:", data.status);
      console.log("Response Data Type:", typeof data.data);
      console.log("Response Data:", data.data);

      if (data.status) {
        let permissionsData = {};

        // Check if data is a base64 encoded string
        if (data.data && typeof data.data === "string") {
          try {
            console.log("Attempting to decode base64 string...");
            const decodedString = atob(data.data);
            console.log("Decoded String:", decodedString);
            const decodedData = JSON.parse(decodedString);
            console.log("Parsed Decoded Data:", decodedData);

            // Extract permissions from the nested structure
            if (decodedData.permission) {
              console.log("Found permission key in decoded data");
              const permissionObj = JSON.parse(decodedData.permission);
              console.log("Final Permission Object:", permissionObj);
              permissionsData = permissionObj;
            }
          } catch (decodeError) {
            console.error("Error decoding base64 data:", decodeError);
          }
        } else if (data.data) {
          // Direct object structure
          console.log("Using direct object structure");
          permissionsData = data.data;
        }

        console.log("Final Permissions Data to be set:", permissionsData);
        setPermissionStructure(permissionsData);
      } else {
        console.error("Failed to fetch permissions:", data.message);
        // Fallback to default structure if API fails
        setPermissionStructure({
          dashboard: {
            icon: faShieldAlt,
            title: "Dashboard",
            permissions: {
              purchase_credit: "Purchase Credit",
              connect_whatsapp: "Connect WhatsApp",
            },
          },
        });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      // Fallback to default structure if API fails
      setPermissionStructure({
        dashboard: {
          icon: faShieldAlt,
          title: "Dashboard",
          permissions: {
            purchase_credit: "Purchase Credit",
            connect_whatsapp: "Connect WhatsApp",
          },
        },
      });
    }
  };

  // Icon mapping for different permission categories
  const getIconForCategory = (categoryKey) => {
    const iconMap = {
      dashboard: faShieldAlt,
      broadcast_campaign: faBroadcastTower,
      users: faUsers,
      chatbot: faRobot,
      templates: faFileAlt,
      whatsapp_profile: faComments,
      whatsapp_chats: faComments,
      settings: faCog,
      billing: faCreditCard,
    };
    return iconMap[categoryKey] || faShieldAlt;
  };

  // Initialize default permissions structure (all false)
  const getDefaultPermissions = () => {
    const defaultPermissions = {};

    const processCategory = (category) => {
      // Handle direct permissions in category
      if (category && typeof category === "object") {
        Object.keys(category).forEach((key) => {
          if (typeof category[key] === "boolean") {
            // This is a direct permission
            defaultPermissions[key] = false;
          } else if (typeof category[key] === "object") {
            // This is a subcategory
            Object.keys(category[key]).forEach((subKey) => {
              if (typeof category[key][subKey] === "boolean") {
                // This is a permission in subcategory
                defaultPermissions[subKey] = false;
              }
            });
          }
        });
      }
    };

    Object.keys(permissionStructure).forEach((categoryKey) => {
      processCategory(permissionStructure[categoryKey]);
    });

    return defaultPermissions;
  };

  // Fetch all roles
  const fetchRoles = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${domain()}/agen_role`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.status) {
        let rolesData = [];
        if (Array.isArray(data.data)) {
          rolesData = data.data;
        } else if (data.data && Array.isArray(data.data.data)) {
          rolesData = data.data.data;
        } else if (data.data && Array.isArray(data.data.roles)) {
          rolesData = data.data.roles;
        } else if (Array.isArray(data.roles)) {
          rolesData = data.roles;
        } else {
          rolesData = [];
        }

        setRoles(rolesData);
      } else {
        console.error("Failed to fetch roles:", data.message);
        setRoles([]);
      }
    } catch (error) {
      setRoles([]);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      await Promise.all([fetchRoles(), fetchPermissions()]);
      setLoading(false);
    };
    initializeData();
  }, []);

  const handleCreateRole = () => {
    navigate("/client-area/whatsapp/user-roles/create");
  };

  const handleEditRole = (roleId) => {
    navigate(`/client-area/whatsapp/user-roles/edit/${roleId}`);
  };

  const [viewingRole, setViewingRole] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [viewingRoleLoading, setViewingRoleLoading] = useState(false);

  const handleViewRole = async (role) => {
    setViewingRoleLoading(true);
    setShowViewModal(true);

    try {
      const token = getToken();
      const response = await fetch(`${domain()}/agen_role/${btoa(role.id)}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.status) {
        let roleData = data.data;

        // Check if data is encrypted (base64 encoded string)
        if (typeof roleData === "string") {
          try {
            console.log("Attempting to decode encrypted role data...");
            const decodedString = atob(roleData);
            console.log("Decoded String:", decodedString);
            roleData = JSON.parse(decodedString);
            console.log("Parsed Decoded Role Data:", roleData);
          } catch (decodeError) {
            console.error("Error decoding role data:", decodeError);
            Swal.fire(
              "Error!",
              "Failed to decode role data. Please try again.",
              "error"
            );
            setShowViewModal(false);
            return;
          }
        }

        setViewingRole(roleData);
      } else {
        Swal.fire(
          "Error!",
          `Failed to fetch role details: ${data.message}`,
          "error"
        );
        setShowViewModal(false);
      }
    } catch (error) {
      console.error("Error fetching role data:", error);
      Swal.fire(
        "Error!",
        "Error fetching role details. Please try again.",
        "error"
      );
      setShowViewModal(false);
    } finally {
      setViewingRoleLoading(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this! This will delete the role and all associated permissions.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const token = getToken();
        const response = await fetch(`${domain()}/agen_role/${btoa(roleId)}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (data.status) {
          setRoles(
            Array.isArray(roles)
              ? roles.filter((role) => role.id !== roleId)
              : []
          );
          Swal.fire(
            "Deleted!",
            "Role has been deleted successfully.",
            "success"
          );
        } else {
          Swal.fire(
            "Error!",
            `Failed to delete role: ${data.message}`,
            "error"
          );
        }
      } catch (error) {
        Swal.fire("Error!", "Error deleting role. Please try again.", "error");
      }
    }
  };

  const filteredRoles = Array.isArray(roles)
    ? roles.filter((role) =>
        role.role_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  if (loading) {
    return (
      <div className="main-panel w-100">
        <div className="content-wrapper">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <p className="mt-2">Loading roles...</p>
                  <Preloader action={loading} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-panel w-100">
      <Preloader action={loading} />
      <div className="content-wrapper">
        <div className="row">
          <div className="col-12">
            <div
              className="card border-0 shadow-sm"
              style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}
            >
              <div
                className="card-header bg-white border-bottom-0 pb-0"
                style={{ borderRadius: "0.75rem 0.75rem 0 0" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4
                      className="card-title mb-1"
                      style={{ color: "#2c3e50", fontWeight: "600" }}
                    >
                      User Roles
                    </h4>
                    <p
                      className="text-muted mb-0"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Manage agent roles and permissions
                    </p>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateRole}
                    style={{
                      borderRadius: "0.5rem",
                      fontWeight: "500",
                      padding: "0.5rem 1.25rem",
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Create New Role
                  </button>
                </div>
              </div>

              <div className="card-body">
                {/* Search Bar */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span
                          className="input-group-text"
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #dee2e6",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faSearch}
                            style={{ color: "#6c757d" }}
                          />
                        </span>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search roles..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ border: "1px solid #dee2e6" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Roles Table */}
                <div className="table-responsive" style={{ borderRadius: '12px' }}>
                  <table className="table payment-history-table">
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th
                          style={{
                            border: "none",
                            fontWeight: "600",
                            color: "#495057",
                          }}
                        >
                          Role Name
                        </th>

                        <th
                          style={{
                            border: "none",
                            fontWeight: "600",
                            color: "#495057",
                          }}
                        >
                          Created
                        </th>
                        <th
                          style={{
                            border: "none",
                            fontWeight: "600",
                            color: "#495057",
                            textAlign: "center",
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRoles.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            <div style={{ color: "#6c757d" }}>
                              <FontAwesomeIcon
                                icon={faUsers}
                                size="2x"
                                className="mb-3"
                              />
                              <p className="mb-0">No roles found</p>
                              <small>
                                Create your first role to get started
                              </small>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredRoles.map((role) => (
                          <tr
                            key={role.id}
                            style={{ borderBottom: "1px solid #f1f3f4" }}
                          >
                            <td
                              style={{
                                padding: "1rem 0.75rem",
                                verticalAlign: "middle",
                                fontWeight: "500",
                                color: "#2c3e50",
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    backgroundColor: "#075E54",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: "0.75rem",
                                    color: "white",
                                    fontSize: "0.875rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  {role.role_name?.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div
                                    style={{
                                      fontWeight: "600",
                                      color: "#2c3e50",
                                    }}
                                  >
                                    {role.role_name}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td
                              style={{
                                padding: "1rem 0.75rem",
                                verticalAlign: "middle",
                                color: "#6c757d",
                                fontSize: "0.875rem",
                              }}
                            >
                              {role.created_at
                                ? new Date(role.created_at).toLocaleDateString()
                                : "N/A"}
                            </td>
                            <td
                              style={{
                                padding: "1rem 0.75rem",
                                verticalAlign: "middle",
                                textAlign: "center",
                              }}
                            >
                              <div className="btn-group" role="group">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => handleViewRole(role)}
                                  style={{ marginRight: "0.25rem" }}
                                  title="View Role Details"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleEditRole(role.id)}
                                  style={{ marginRight: "0.25rem" }}
                                  title="Edit Role"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteRole(role.id)}
                                  title="Delete Role"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Role Modal */}
      {showViewModal && viewingRole && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                  Role Details: {viewingRole.role_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingRole(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="fw-bold text-primary mb-3">
                      Basic Information
                    </h6>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Role Name:
                      </label>
                      <p className="form-control-plaintext border rounded p-2 bg-light">
                        {viewingRole.role_name}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Created:</label>
                      <p className="form-control-plaintext border rounded p-2 bg-light">
                        {viewingRole.created_at
                          ? new Date(
                              viewingRole.created_at
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Last Updated:
                      </label>
                      <p className="form-control-plaintext border rounded p-2 bg-light">
                        {viewingRole.updated_at
                          ? new Date(
                              viewingRole.updated_at
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold text-primary mb-3">Permissions</h6>
                    <div className="permissions-display">
                      {(() => {
                        try {
                          const permissions =
                            typeof viewingRole.permissions === "string"
                              ? JSON.parse(viewingRole.permissions)
                              : viewingRole.permissions;

                          if (!permissions || typeof permissions !== "object") {
                            return (
                              <p className="text-muted">
                                No permissions configured
                              </p>
                            );
                          }

                          return Object.entries(permissions).map(
                            ([category, perms]) => (
                              <div key={category} className="mb-3">
                                <h6 className="text-secondary fw-semibold mb-2">
                                  {category
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </h6>
                                <div className="d-flex flex-wrap gap-2">
                                  {Object.entries(perms).map(
                                    ([perm, value]) => (
                                      <span
                                        key={perm}
                                        className={`badge ${
                                          value ? "bg-success" : "bg-secondary"
                                        } me-2 mb-1`}
                                        style={{ fontSize: "0.75rem" }}
                                      >
                                        {perm
                                          .replace(/_/g, " ")
                                          .replace(/\b\w/g, (l) =>
                                            l.toUpperCase()
                                          )}
                                        : {value ? "Yes" : "No"}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            )
                          );
                        } catch (error) {
                          return (
                            <p className="text-danger">
                              Error parsing permissions
                            </p>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingRole(null);
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditRole(viewingRole.id);
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Edit Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserRoles;
